import * as Icons from '@ant-design/icons';
import axios from 'axios';
import { Button, Progress, Upload, UploadFile, notification } from 'antd';
import { useCreate, useNotification } from '@refinedev/core';
import { useEffect, useState } from 'react';

type File = undefined | UploadFile;
type Preview = undefined | string;

interface MaxSize {
  size: number;
  message?: string;
}

interface Props {
  accept: string | undefined;
  type:
    | 'instructor'
    | 'product'
    | 'product_category'
    | 'course'
    | 'testimonial'
    | 'hero_banner'
    | 'dam_video'
    | 'thumbnail'
    | 'certifier_signature'
    | 'offer';
  onChange?: (url: string | undefined) => void;
  onValidate?: (file: UploadFile) => Promise<boolean>;
  src?: Preview;
  disabled?: boolean;
  maxSize?: MaxSize;
}

interface Asset {
  url: string;
  upload_url: string;
  id?: string;
  options?: {
    upload_url: string;
  }[];
}

const AssetUpload = ({
  accept,
  type,
  onChange,
  src,
  onValidate,
  disabled,
  maxSize
}: Props) => {
  const [file, setFile] = useState<File | null>();
  const [preview, setPreview] = useState<Preview>(src);
  const [isLoading, setIsLoading] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadPercentage, setUploadPercentage] = useState(0);
  const { mutate } = useCreate<Asset>();

  const fileExtension = file ? file.name.split('.').pop() : undefined;
  const fileName = file ? file.name : undefined;
  const previewExtension = preview ? preview.split('.').pop() : undefined;

  const { open } = useNotification();

  const createAsset = () => {
    if (type === 'dam_video') {
      return new Promise<Asset>((resolve, reject) => {
        mutate(
          {
            resource: `prometheus/files`,
            values: { name: fileName, content_type: 'mp4' }
          },
          {
            onSuccess: ({ data }) => resolve(data),
            onError: error => reject(error)
          }
        );
      });
    }
    return new Promise<Asset>((resolve, reject) => {
      mutate(
        {
          resource: 'assets',
          values: { type, extension: fileExtension }
        },
        {
          onSuccess: ({ data }) => resolve(data),
          onError: error => reject(error)
        }
      );
    });
  };

  const upload = async () => {
    try {
      const isValid = file && (await isValidFile(file));

      if (file && !isValid) {
        if (!onValidate) {
          open?.({
            type: 'error',
            message: 'Arquivo inválido!',
            description: 'Verifique a descrição do campo',
            key: `${file.fileName}-error-upload`
          });
        }

        setFile(null);
        setIsLoading(false);

        return;
      }

      const { url, upload_url, id, options } = await createAsset();

      setUploadPercentage(0);
      setIsUploading(true);

      if (type === 'dam_video') {
        await axios.put(options![0].upload_url, file, {
          onUploadProgress: ({ loaded, total }) => {
            setUploadPercentage(Math.round((100 * loaded) / total));
          }
        });
        setPreview(id);
      }

      if (type !== 'dam_video') {
        await axios.put(upload_url, file, {
          onUploadProgress: ({ loaded, total }) => {
            setUploadPercentage(Math.round((100 * loaded) / total));
          }
        });
        setPreview(url);
      }
    } catch {
      setIsUploading(false);
    }

    setIsLoading(false);
  };

  const isValidFile = async (file: File) => {
    if (onValidate && file) {
      return await onValidate(file);
    }

    return true;
  };

  const remove = () => {
    setPreview('');
    setFile(null);
    setUploadPercentage(0);
    setIsUploading(false);
  };

  useEffect(() => {
    setIsLoading(true);

    if (file) {
      upload();
    } else {
      setIsLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [file]);

  useEffect(() => {
    setPreview(src);
  }, [src]);

  useEffect(() => {
    onChange?.(preview);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preview]);

  const beforeUpload = async (newFile: UploadFile) => {
    if (maxSize && newFile.size !== undefined) {
      const isFileTooLarge = newFile.size / 1024 / 1024 > maxSize.size;

      if (isFileTooLarge) {
        notification.error({
          message: 'Erro de Upload',
          description:
            maxSize.message || `O arquivo deve ter no máximo ${maxSize.size}MB`,
          duration: 3
        });
        return Upload.LIST_IGNORE;
      }
    }

    setFile(newFile);
    return false;
  };

  return (
    <div className="flex flex-col gap-4 w-64">
      {preview && ['png', 'jpg', 'jpeg', 'svg'].includes(previewExtension!) && (
        <img src={preview} className="w-64" alt="asset" />
      )}

      {preview && (
        <Button
          danger
          className="!flex items-center justify-center"
          disabled={isLoading || disabled}
          onClick={remove}
        >
          <Icons.CloseCircleOutlined />
          Remover
        </Button>
      )}

      <Upload
        className="w-full"
        maxCount={1}
        accept={accept}
        fileList={[]}
        onRemove={() => setFile(null)}
        beforeUpload={beforeUpload}
      >
        <Button
          className="!flex items-center justify-center w-full"
          disabled={isLoading || disabled}
        >
          <Icons.UploadOutlined />
          Upload
        </Button>
      </Upload>
      {isUploading && <Progress percent={uploadPercentage} />}
    </div>
  );
};

export default AssetUpload;
